import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { useState } from "react";
import {
  useAddOption,
  useDeleteOption,
  usePropertyOptions,
  useReorderOptions,
  useUpdateOption,
  type ProfileProperty,
} from "./api";
import { useNotify } from "../../app/useNotify";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { errorMessageFor } from "../../api/client";

interface Props {
  property: ProfileProperty;
  onClose: () => void;
}

export function PropertyOptionsDialog({ property, onClose }: Props) {
  const notify = useNotify();
  const options = usePropertyOptions(property.apiUrl);
  const add = useAddOption(property.apiUrl);
  const update = useUpdateOption(property.apiUrl);
  const remove = useDeleteOption(property.apiUrl);
  const reorder = useReorderOptions(property.id, property.keyName, property.apiUrl);

  // null until the user reorders; the server's order shows through until then.
  const [order, setOrder] = useState<number[] | null>(null);
  const [newLabel, setNewLabel] = useState("");
  const [allowInput, setAllowInput] = useState(false);
  const [edits, setEdits] = useState<Record<number, string>>({});
  const [deleting, setDeleting] = useState<{ id: number; label: string } | null>(null);

  const serverOrder = (options.data ?? []).map((option) => option.id);
  const effectiveOrder = order && order.length === serverOrder.length ? order : serverOrder;
  const byId = new Map((options.data ?? []).map((option) => [option.id, option]));
  const ordered = effectiveOrder.map((id) => byId.get(id)).filter(Boolean);
  const dirty = effectiveOrder.join(",") !== serverOrder.join(",");

  function move(index: number, delta: number) {
    const next = [...effectiveOrder];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setOrder(next);
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{property.label}</DialogTitle>
      <DialogContent>
        {options.isLoading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <List dense>
            {ordered.map((option, index) => (
              <ListItem key={option!.id} disableGutters>
                <Stack direction="row" sx={{ gap: 1, alignItems: "center", width: "100%" }}>
                  <TextField
                    size="small"
                    fullWidth
                    value={edits[option!.id] ?? option!.label}
                    slotProps={{ htmlInput: { "aria-label": `Label for ${option!.label}` } }}
                    onChange={(event) =>
                      setEdits((previous) => ({ ...previous, [option!.id]: event.target.value }))
                    }
                  />
                  <Tooltip title="Save">
                    <span>
                      <IconButton
                        size="small"
                        aria-label={`Save ${option!.label}`}
                        disabled={edits[option!.id] === undefined || update.isPending}
                        onClick={async () => {
                          try {
                            await update.mutateAsync({
                              ...option!,
                              label: edits[option!.id] ?? option!.label,
                            });
                            setEdits((previous) => {
                              const next = { ...previous };
                              delete next[option!.id];
                              return next;
                            });
                            notify.success("Option renamed");
                          } catch (error) {
                            notify.error(errorMessageFor(error) ?? "Could not update the option.");
                          }
                        }}
                      >
                        <SaveIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <IconButton
                    size="small"
                    aria-label={`Move ${option!.label} up`}
                    onClick={() => move(index, -1)}
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={`Move ${option!.label} down`}
                    onClick={() => move(index, 1)}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={`Delete ${option!.label}`}
                    onClick={() => setDeleting({ id: option!.id, label: option!.label })}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </ListItem>
            ))}
          </List>
        )}

        <Stack direction="row" sx={{ gap: 1, alignItems: "center", mt: 2 }}>
          <TextField
            label="New option"
            size="small"
            fullWidth
            value={newLabel}
            onChange={(event) => setNewLabel(event.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={allowInput}
                onChange={(event) => setAllowInput(event.target.checked)}
              />
            }
            label="Allow input"
          />
          <Button
            variant="contained"
            disabled={!newLabel.trim() || add.isPending}
            onClick={async () => {
              try {
                await add.mutateAsync({ label: newLabel.trim(), allowInput });
                setNewLabel("");
                setAllowInput(false);
                notify.success("Option added");
              } catch (error) {
                notify.error(errorMessageFor(error) ?? "Could not add the option.");
              }
            }}
          >
            Add
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={!dirty || reorder.isPending}
          onClick={async () => {
            try {
              await reorder.mutateAsync(effectiveOrder);
              setOrder(null);
              notify.success("Order saved");
            } catch (error) {
              notify.error(errorMessageFor(error) ?? "Could not save the order.");
            }
          }}
        >
          Save order
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <ConfirmDialog
        open={deleting !== null}
        title={`Remove "${deleting?.label}"?`}
        message="New members will no longer see it as an answer on the registration form. This cannot be undone."
        confirmLabel="Remove"
        destructive
        busy={remove.isPending}
        onClose={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await remove.mutateAsync(deleting.id);
            notify.success("Option removed");
          } catch (error) {
            notify.error(errorMessageFor(error) ?? "Could not remove the option.");
          } finally {
            setDeleting(null);
          }
        }}
      />
    </Dialog>
  );
}
